import React, { useState, useContext, useCallback, memo } from 'react'
import TransparencySlider from '../TransparencySlider/TransparencySlider'
import { useTranslation } from 'react-i18next'
import { useGlobalVariablesContext } from '../../context/GlobalVariablesContext'
import MapContext from '../Map/MapContext'
import GeoJSON from 'ol/format/GeoJSON'
import 'bootstrap-icons/font/bootstrap-icons.css'
import { getLayerUrl } from '../helpers/getLayerUrl'
import { useToken } from '../../hooks/useToken'
import { isAppDomain } from '../../utils/misc'


const LayerTools = ({ layer, opacities, handleOpacityChange }) => {
    const { t } = useTranslation()
    const { config } = useGlobalVariablesContext()
    const { map } = useContext(MapContext)
    const { token } = useToken()

    const [isRefreshing, setIsRefreshing] = useState(false)
    const [alert, setAlert] = useState(null)

    const handleRefreshLayer = useCallback(async () => {
        if (!map || !config || !layer) return

        setIsRefreshing(true)

        const mapLayers = map.getLayers().getArray()
        const layerName = layer.name
        const matchedLayer = mapLayers.find(l => l.get('name') === layerName)

        if (matchedLayer && typeof matchedLayer.getSource === 'function') {
            const source = matchedLayer.getSource()
            const url = getLayerUrl(matchedLayer)

            console.debug(`🌐 URL detectada para la capa "${layerName}":`, url)

            if (url) {
                try {
                    // Prepare fetch options with authentication if needed
                    const fetchOptions = {
                        headers: {
                            'Accept': 'application/json',
                        },
                    };

                    // Add authentication headers if token is available and URL is from the same domain
                    if (token && isAppDomain(url)) {
                        fetchOptions.headers['Authorization'] = `Bearer ${token}`;
                    }

                    const response = await fetch(url, fetchOptions)
                    const data = await response.json()

                    if (data && data.features) {
                        console.debug(`📄 Features recibidos (${data.features.length}):`, data.features)

                        const targetSource = source.getSource ? source.getSource() : source
                        targetSource.clear()
                        const features = new GeoJSON().readFeatures(data, {
                            dataProjection: 'EPSG:4326',
                            featureProjection: map.getView().getProjection()
                        });

                        targetSource.addFeatures(features);

                        if (features.length === 1) {
                            const extent = features[0].getGeometry().getExtent();
                            map.getView().fit(extent, {
                                padding: [100, 100, 100, 100],
                                duration: 1000,
                                maxZoom: 17
                            });
                        }


                        setAlert({ type: 'success', message: t('Layer refreshed successfully') })
                    } else {
                        console.warn('⚠️ No se encontraron features.')
                        setAlert({ type: 'warning', message: t('No features found') })
                    }
                } catch (error) {
                    console.error('❌ Error al hacer fetch de la capa:', error)
                    setAlert({ type: 'danger', message: t('Error fetching layer data') })
                }
            } else {
                console.warn(`⚠️ No se pudo determinar la URL de la capa "${layerName}".`)
                setAlert({ type: 'danger', message: t('No refresh method or URL available') })
            }
        } else {
            console.warn(`⚠️ No se encontró la capa con nombre "${layerName}" en el mapa.`)
            setAlert({ type: 'danger', message: t('Layer not found on the map') })
        }

        setTimeout(() => {
            setIsRefreshing(false)
            setAlert(null)
        }, 2000)
    }, [map, config, layer, token, t])

    const tools = (
        <>
            <TransparencySlider
                opacity={opacities[layer.name] || layer.opacity || 0.8}
                onChangeOpacity={(newOpacity) => handleOpacityChange(layer.name, newOpacity, layer)}
            />
            <span
                className="info-icon bi bi-question-circle"
                title={layer.queryable ? t('Queryable layer') : t('Non-queryable layer')}
                style={{ fontSize: '14px', marginLeft: '2px', cursor: layer.queryable ? 'help' : 'default', opacity: layer.queryable ? 1 : 0.3 }}
            />
            <span
                className="bi bi-info-circle"
                title={layer.abstract || ''}
                style={{ fontSize: '14px', marginLeft: '4px', cursor: layer.abstract ? 'help' : 'default', opacity: layer.abstract ? 1 : 0.3 }}
            />

            {/* refrescar capa */}
            <button
                onClick={handleRefreshLayer}
                title={t('Refresh layer')}
                className="btn btn-link btn-sm p-0 ms-2"
                style={{ fontSize: '1.2em', cursor: 'pointer' }}
                disabled={isRefreshing}
            >
                {isRefreshing ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                    <i className="bi bi-arrow-clockwise"></i>
                )}
            </button>
         
        </>
    )

    return (
        <div className="d-flex flex-column align-items-end">
            {alert && (
                <div className={`alert alert-${alert.type} py-1 px-2 mb-2`} style={{ fontSize: '0.85em' }}>
                    {alert.message}
                </div>
            )}
            <div className="d-flex justify-content-end align-items-center">
                {tools}
            </div>
        </div>
    )
}

export default memo(LayerTools)
